import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { ContentListItemDTO } from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { SectionHeading, Loading, EmptyState } from "../components/ui";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: "記事",
  video: "動画",
  pdf: "PDF",
  external_link: "外部リンク",
};

const CATEGORIES = [
  { code: "", label: "すべて" },
  { code: "archive", label: "アーカイブ動画" },
  { code: "interview", label: "インタビュー記事" },
  { code: "lecture", label: "講義" },
  { code: "case", label: "事例" },
  { code: "document", label: "資料" },
] as const;

export function ContentsPage() {
  const [items, setItems] = useState<ContentListItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  const load = (cat: string) => {
    setLoading(true);
    const params = cat ? `?category=${cat}` : "";
    api
      .get<{ items: ContentListItemDTO[] }>(`/contents${params}`)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(category);
  }, [category]);

  return (
    <div>
      <SectionHeading level={2}>会員限定コンテンツ</SectionHeading>

      {/* カテゴリタブ */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => (
          <button
            key={c.code}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              category === c.code
                ? "bg-tora text-white"
                : "bg-black/5 text-tora-ink/70 hover:bg-black/10"
            }`}
            onClick={() => setCategory(c.code)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState
          icon="🎥"
          title={
            category
              ? `「${CATEGORIES.find((c) => c.code === category)?.label ?? category}」のコンテンツはまだありません`
              : "公開コンテンツはまだありません"
          }
        />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((c) => {
            const thumb = uploadsUrl(c.thumbnailKey);
            const typeLabel =
              CONTENT_TYPE_LABELS[c.contentType] ?? c.contentType;
            return (
              <li key={c.id} className="h-full">
                <Link
                  to={`/contents/${c.id}`}
                  className="card h-full flex flex-col overflow-hidden group transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tora"
                >
                  <div className="relative aspect-[16/10] bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="w-full h-full object-cover object-top md:group-hover:scale-[1.03] transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-tora-ink/30 text-lg tracking-widest">
                        準備中
                      </div>
                    )}
                    {c.contentType === "video" && thumb && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center text-xl">
                          ▶
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-tora-ink/60 shadow-sm">
                      🔒
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col p-4 md:p-5">
                    <h3 className="text-base md:text-lg font-bold text-tora-ink leading-snug group-hover:text-tora transition-colors line-clamp-2">
                      {c.title}
                    </h3>
                    <div className="mt-auto pt-3 flex items-center gap-2">
                      <span className="inline-block rounded-full border border-tora text-tora text-xs px-3 py-1 font-medium">
                        {typeLabel}
                      </span>
                      {c.categoryCode && (
                        <span className="inline-block rounded-full bg-black/5 text-tora-ink/60 text-xs px-3 py-1">
                          {CATEGORIES.find((cat) => cat.code === c.categoryCode)?.label ?? c.categoryCode}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
